import { IApiUserGroupParams } from 'multirpc-sdk';

import { JWT } from 'domains/jwtToken/types';
import { createNotifyingQueryFn } from 'store/utils/createNotifyingQueryFn';
import { createQuerySelectors } from 'store/utils/createQuerySelectors';
import { web3Api } from 'store/queries';

import { IProjectWithBlockchains } from '../types';
import {
  fetchProjectWhitelistBlockchains,
  fetchProjectWhitelistBlockchainsFallback,
} from './fetchProjectWhitelistBlockchains';

export interface IFetchProjectsWhitelistsBlockchainsParams
  extends IApiUserGroupParams {
  projects: JWT[];
}

// The endpoint name is listed in endpointsSerializedByParams constant
// in packages/protocol/src/store/queries/index.ts file.
// If the name has changed it should be reflected there as well.
export const {
  endpoints: { fetchProjectsWhitelistsBlockchains },
  useFetchProjectsWhitelistsBlockchainsQuery,
  useLazyFetchProjectsWhitelistsBlockchainsQuery,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    fetchProjectsWhitelistsBlockchains: build.query<
      IProjectWithBlockchains[],
      IFetchProjectsWhitelistsBlockchainsParams
    >({
      queryFn: createNotifyingQueryFn(
        async ({ group, projects }, { dispatch }) => {
          const data = await Promise.all(
            projects.map(project => {
              const fetch = async (): Promise<IProjectWithBlockchains> => {
                const { userEndpointToken: token } = project;
                const {
                  data: blockchains = fetchProjectWhitelistBlockchainsFallback,
                } = await dispatch(
                  fetchProjectWhitelistBlockchains.initiate(
                    { group, token },
                    { forceRefetch: true },
                  ),
                );

                return { blockchains, ...project };
              };

              return fetch();
            }),
          );

          return { data };
        },
      ),
    }),
  }),
  overrideExisting: true,
});

const fetchProjectsWhitelistsBlockchainsFallback: IProjectWithBlockchains[] =
  [];

export const {
  selectDataWithFallbackCachedByParams: selectProjectsWhitelistsBlockchains,
  selectLoadingCachedByParams: selectProjectsWhitelistsBlockchainsLoading,
  selectStateCachedByParams: selectProjectsWhitelistsBlockchainsState,
} = createQuerySelectors({
  endpoint: fetchProjectsWhitelistsBlockchains,
  fallback: fetchProjectsWhitelistsBlockchainsFallback,
});
